// ─────────────────────────────────────────────────────────────────────────────
// Oriora iOS native kit — the SHARED App-Store pieces (extracted from Expense, the
// proven source, 2026-06-14). Copied verbatim into this app's src/ from
// champion-lifecycle/native-app-kit/helpers/ios-native-kit.tsx (only the supabase
// import path is local: "./supabase"). USE THIS — do not re-derive.
//
// Pairs with the Rust/Cargo wiring (done in this app):
//   Cargo.toml:  tauri-plugin-opener = "2"
//                tauri-plugin-web-auth = { git = "…", rev = "b08de8276eb7d84522fc3322c09c71b4cabc904e" }
//   src-tauri/src/lib.rs:  .plugin(tauri_plugin_opener::init()).plugin(tauri_plugin_web_auth::init())
//   capabilities/default.json:  "web-auth:allow-authenticate"
//   tauri.conf.json → plugins.deep-link "mobile" block for scheme "interview-prep-tutor".
// Full per-app iOS bring-up + the 9 silent-failure gotchas: memory oriora-ios-build-and-signin-fix.
// ─────────────────────────────────────────────────────────────────────────────
import { invoke } from "./tauri-web";
import { supabase } from "./supabase"; // this app's existing supabase client

const PRIVACY_URL = "https://orioralabs.com/about/privacy";
const ACCOUNT_DELETE_URL = "https://orioralabs.com/account/delete"; // APP-READY-STANDARD §2 (Guideline 5.1.1v)

// §2/§3 — AI-data consent gate (Apple Guideline 5.1.1(i)/5.1.2). Shown ONCE (localStorage),
// names the data + vendors + purpose, links Privacy. Render it before any AI call:
//   const [ok,setOk] = useState(()=>localStorage.getItem(AI_CONSENT_KEY)==="1");
//   if (!ok) return <AiConsent brand="Oriora Interview"
//                              onAccept={()=>{ localStorage.setItem(AI_CONSENT_KEY,"1"); setOk(true); }} />;
export const AI_CONSENT_KEY = "oriora.ai_consent.v1";

// 🔒 LOCKED NOTICE — one wording, every app; ONLY the brand varies. There is deliberately
// NO per-app data slot: a `dataNote` prop used to inject app-specific text (and absolute
// "never leaves your device" claims) into the notice. Do not re-add it.
export function AiConsent({ brand, onAccept }: { brand: string; onAccept: () => void }) {
  const openPrivacy = () => { void invoke("open_external", { url: PRIVACY_URL }); };
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.25rem, env(safe-area-inset-top)) 1.25rem max(1.25rem, env(safe-area-inset-bottom))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2rem", margin: "auto" }}>
        <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: "var(--fs-sm)", textAlign: "center" }}>{brand}</div>
        <h1 style={{ fontSize: "1.4rem", margin: "0.5rem 0 0.85rem", textAlign: "center" }}>How Oriora uses AI</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.6, marginBottom: "0.85rem" }}>
          When you use an AI feature, {brand} sends the data needed for that action to third-party
          AI providers (Anthropic, OpenAI, Google, and others) to produce the result.
        </p>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.6, marginBottom: "1.25rem" }}>
          Your data stays yours — we don't retain it: data passes through Oriora for each AI action and isn't kept by us. You choose where it lives — this device, and optionally your own GitHub or Google Drive. By continuing, you agree to this.
        </p>
        <button className="btn btn-primary" style={{ width: "100%", marginBottom: "0.6rem" }} onClick={onAccept}>I Agree</button>
        <button className="btn-ghost" style={{ width: "100%" }} onClick={openPrivacy}>Privacy Policy</button>
      </div>
    </main>
  );
}

// §2 (#61, Guideline 4) — in-app OAuth via ASWebAuthenticationSession (NOT external Safari).
// Use in the login button: `if (platform() === "ios") await signInIos(appScheme); else await openWebLogin();`
export async function signInIos(appScheme: string): Promise<void> {
  const { callbackUrl } = await invoke<{ callbackUrl: string }>("plugin:web-auth|authenticate", {
    payload: { url: `https://orioralabs.com/login?return=${appScheme}`, callbackScheme: appScheme },
  });
  const frag = new URLSearchParams(new URL(callbackUrl).hash.replace(/^#/, ""));
  const access_token = frag.get("access_token");
  const refresh_token = frag.get("refresh_token");
  if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
}

// §2 (#61, Guideline 4) — macOS in-app OAuth via native ASWebAuthenticationSession
// (open_native_auth_session, Rust→Swift). The system sheet — not an embedded WebView — is what
// Google/GitHub accept, so email-code AND Google/GitHub/Apple all complete inside the app (no
// external browser). Use: `else if (platform() === "macos") await signInMac(appScheme);`
export async function signInMac(appScheme: string): Promise<void> {
  const callbackUrl = await invoke<string>("open_native_auth_session", {
    url: `https://orioralabs.com/login?return=${appScheme}`,
    scheme: appScheme,
  });
  const frag = new URLSearchParams(new URL(callbackUrl).hash.replace(/^#/, ""));
  const access_token = frag.get("access_token");
  const refresh_token = frag.get("refresh_token");
  if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
}

// §2 (Guideline 5.1.1v) — in-app account-deletion link (required on every App-Store app).
export function openAccountDelete(): void { void invoke("open_external", { url: ACCOUNT_DELETE_URL }); }

// ─────────────────────────────────────────────────────────────────────────────
// GitHub-restore device OAuth — connects THIS device directly to the customer's own GitHub for
// backup restore (bypasses the server relay's ~1MB read ceiling; see `~/Desktop/backup restore
// over 1MB — suspected issue.md`). Reuses the SAME native auth-session runners as signInIos/
// signInMac above — the only new pieces are: calling oriora-server's /github-restore/start with a
// device-generated nonce, and verifying that SAME nonce comes back before ever trusting a token
// (the CSRF binding — see github-restore-oauth.ts server-side; skipping this check reopens a real,
// previously-found OAuth login-CSRF gap, not a theoretical one). The resulting token is stored via
// the Keychain commands in keychain.rs, never sent anywhere else.
export class GithubRestoreError extends Error {}

async function startGithubRestore(appScheme: string): Promise<{ authorizeUrl: string; nonce: string }> {
  const nonce = crypto.randomUUID();
  const r = await fetch(`https://api.orioralabs.com/github-restore/start?app=${encodeURIComponent(appScheme)}&nonce=${encodeURIComponent(nonce)}`);
  if (!r.ok) throw new GithubRestoreError("Couldn't start the GitHub connection — try again.");
  const { authorizeUrl } = (await r.json()) as { authorizeUrl: string };
  return { authorizeUrl, nonce };
}

/** Parse the callback URL, verify the nonce matches THIS request, and store the token in
 *  Keychain. Throws GithubRestoreError on any mismatch — never stores an unverified token. */
async function finishGithubRestore(callbackUrl: string, expectedNonce: string): Promise<void> {
  const url = new URL(callbackUrl);
  const returnedNonce = url.searchParams.get("nonce");
  const frag = new URLSearchParams(url.hash.replace(/^#/, ""));
  const token = frag.get("access_token");
  if (!token) throw new GithubRestoreError("GitHub didn't return a token — try connecting again.");
  // The one hard check: a valid signature on the server's state only proves GitHub issued it,
  // NOT that this callback belongs to the request THIS device just made. Reject anything else.
  if (returnedNonce !== expectedNonce) throw new GithubRestoreError("This connection didn't match what we started — try again.");
  await invoke("store_github_restore_token", { token });
}

export async function connectGithubRestoreIos(appScheme: string): Promise<void> {
  const { authorizeUrl, nonce } = await startGithubRestore(appScheme);
  const { callbackUrl } = await invoke<{ callbackUrl: string }>("plugin:web-auth|authenticate", {
    payload: { url: authorizeUrl, callbackScheme: appScheme },
  });
  await finishGithubRestore(callbackUrl, nonce);
}

export async function connectGithubRestoreMac(appScheme: string): Promise<void> {
  const { authorizeUrl, nonce } = await startGithubRestore(appScheme);
  const callbackUrl = await invoke<string>("open_native_auth_session", { url: authorizeUrl, scheme: appScheme });
  await finishGithubRestore(callbackUrl, nonce);
}
